import { defineNuxtPlugin, useState } from '#imports';
import { joinURL } from 'ufo';

import type { ResolvedCurrentCluster } from '~/utils/clusters';

import { EMPTY_CLUSTER } from '~/utils/clusters';

/**
 * Prefix client `/_i18n` fetches with the current cluster path prefix.
 *
 * @nuxtjs/i18n bakes its message endpoint
 * (`/_i18n/<hash>/<locale>/messages.json`) at build time from a single base,
 * with no per-request prefix. On a path-routed cluster the client fetch then
 * hits the apex deployment at Front Door instead of this one. The endpoint is
 * vestigial today (empty messages — real translations load via
 * `plugins/02.loadConfig.ts`), so this is hardening: it keeps the request
 * on-cluster and future-proofs the case where real `files` are added.
 *
 * `enforce: 'pre'` runs this before the i18n module's client plugins (which
 * sort at the default order), so the wrapped `$fetch` is installed before any
 * locale load. The wrap is a no-op on the root cluster (empty prefix) and
 * passes every non-`/_i18n` request through untouched. `$fetch.create`
 * preserves `.raw`/`.create`/`.native` so other consumers are unaffected.
 */
export default defineNuxtPlugin({
  name: 'cluster-fetch-prefix',
  enforce: 'pre',
  setup() {
    const { pathPrefix } = useState<ResolvedCurrentCluster>('cluster', () => EMPTY_CLUSTER).value;
    if (!pathPrefix) {
      return;
    }

    const baseFetch = globalThis.$fetch;
    globalThis.$fetch = baseFetch.create({
      onRequest(context) {
        if (typeof context.request === 'string' && context.request.startsWith('/_i18n')) {
          context.request = joinURL(pathPrefix, context.request);
        }
      },
    });
  },
});
